import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import orderBy from "lodash/orderBy";
import type { ListCalculationsResponseItem, NoteDTO } from "../../api/api";
import { listCalculations, updateCalculationNotes as updateNotesApi } from "../../api/api";
import { useCustomers } from "../customers/useCustomers";

export const useCalculations = (params: { from: string; to?: string }) => {
  const queryClient = useQueryClient();
  const { data: calculations, isLoading } = useQuery({
    queryKey: ["calculations", params],
    queryFn: ({ signal }) => listCalculations(params, signal),
  });
  const { customers, isLoading: areCustomersLoading } = useCustomers();

  const updateNotesMutation = useMutation<void, unknown, { calculationId: string; notes: NoteDTO[] }>({
    mutationFn: (m) => updateNotesApi(m.calculationId, m.notes),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["calculations", params] });
    },
  });

  const updateCalculationNotes = (calculationId: string, notes: NoteDTO[]) =>
    updateNotesMutation.mutate({ calculationId, notes });

  const orderedCalculationsWithCustomers: ListCalculationsResponseItemWithCustomer[] = orderBy(
    calculations,
    (o) => o.startDate,
  ).map((c) => ({
    ...c,
    customer: { id: c.customer.id, name: customers.find((customer) => customer.id === c.customer.id)?.name },
  }));

  return {
    calculations: orderedCalculationsWithCustomers,
    isLoading: isLoading || areCustomersLoading,
    updateCalculationNotes,
  };
};

export interface ListCalculationsResponseItemWithCustomer extends ListCalculationsResponseItem {
  readonly customer: {
    readonly id: string;
    readonly name?: string;
  };
}
